import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
import { useOrder } from '../lib/orderContext';
import { trackAddToCartAndRedirect } from '../lib/metaPixel';
interface OrderBump {
  id: string;
  title: string;
  price: number;
  image: string;
  shortDesc: string;
  details: string;
}
const ORDER_BUMPS: OrderBump[] = [{
  id: 'kit-emergencia',
  title: 'Kit de Emergencia: Adiós a los Bochornos',
  price: 5,
  image: 'https://cdn.magicpatterns.com/uploads/tWSprZyMFSj7jXh6wCe6BP/OrderBump_1_-_Portada.png',
  shortDesc: 'Técnicas inmediatas para reducir la intensidad de los bochornos en el momento en que aparecen. Para tener siempre a la mano.',
  details: 'Incluye un protocolo de respuesta rápida ante los bochornos: técnicas de respiración, ajustes de temperatura corporal, alimentos que los disparan y alimentos que los calman. Diseñado para usarse en el momento exacto en que aparece el calor, sin medicamentos, sin efectos secundarios. Tu kit de bolsillo para los días difíciles.'
}, {
  id: 'lista-super',
  title: 'Lista del Súper Inteligente',
  price: 5,
  image: 'https://cdn.magicpatterns.com/uploads/2xZoVettT3NXDcEwzAVK3w/OrderBump_2_-_Portada.png',
  shortDesc: 'La lista de supermercado definitiva para comprar exactamente lo que necesitas, sin perderte entre productos ni gastar de más.',
  details: 'Una lista organizada por secciones del supermercado con los ingredientes clave del protocolo. Incluye alternativas económicas, marcas accesibles, y tips para leer etiquetas sin confundirte. Ve al súper con seguridad, compra solo lo que tu cuerpo necesita y ahorra tiempo y dinero desde la primera visita.'
}];
export function OrderBumpsSection() {
  const {
    total,
    formattedTotal,
    checkoutUrl
  } = useOrder();
  const goToCheckout = () => {
    trackAddToCartAndRedirect(checkoutUrl, {
      content_name: 'El Protocolo Meno-Metabólico + 3 Bonos',
      content_ids: ['42499535011925'],
      content_type: 'product',
      value: total,
      currency: 'USD'
    });
  };
  return <section className="py-12 md:py-16 bg-[#fdfbf6]" data-id="element-148">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-149">
        <div className="text-center mb-12" data-id="element-150">
          <div className="inline-block bg-[#c9a961]/20 text-[#b8954d] px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-4 border border-[#c9a961]/30" data-id="element-151">
            ⚡ MEJORA TU PROTOCOLO — OFERTA ÚNICA
          </div>
          <p className="text-lg md:text-xl text-[#2d2d2d] font-medium" data-id="element-152">
            Agrégalo hoy por solo $5 USD cada uno
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-153">
          {ORDER_BUMPS.map((bump, idx) => <OrderBumpCard key={bump.id} bump={bump} index={idx} data-id="element-154" />)}
        </div>

        <div className="mt-12 flex justify-center" data-id="element-155">
          <motion.button onClick={goToCheckout} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="w-full md:w-auto bg-[#2d4a3e] text-white px-10 py-5 rounded-lg text-lg md:text-xl font-bold shadow-xl hover:bg-[#1f3a2e] transition-colors" data-id="element-156">
            Descargar Todo Por {formattedTotal}
          </motion.button>
        </div>
      </div>
    </section>;
}
function OrderBumpCard({
  bump,
  index
}: {
  bump: OrderBump;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    toggle,
    isSelected
  } = useOrder();
  const selected = isSelected(bump.id);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay: index * 0.1
  }} onClick={() => toggle(bump.id)} className={`relative bg-white rounded-2xl overflow-hidden flex flex-col cursor-pointer transition-all duration-300 ${selected ? 'border-2 border-solid border-[#5d6e4f] shadow-lg' : 'border-2 border-dashed border-amber-400 shadow-md hover:shadow-lg'}`} data-id="element-157">
      <div className="relative" data-id="element-158">
        <img src={bump.image} alt={bump.title} className="w-full h-48 object-cover" data-id="element-159" />
        <div className="absolute top-4 left-4 bg-[#c9a961] text-[#1f3a2e] px-3 py-1 rounded-full text-xs font-bold tracking-wide shadow-md" data-id="element-160">
          OFERTA ESPECIAL
        </div>
      </div>

      <div className="p-6 flex flex-col flex-grow" data-id="element-161">
        <div className="flex justify-between items-start gap-4 mb-3" data-id="element-162">
          <h3 className="font-serif text-xl text-[#2d2d2d] leading-snug" data-id="element-163">
            {bump.title}
          </h3>
          <span className="font-bold text-[#5d6e4f] whitespace-nowrap" data-id="element-164">
            ${bump.price} USD
          </span>
        </div>

        <p className="text-sm text-[#2d2d2d] leading-relaxed mb-6 flex-grow" data-id="element-165">
          {bump.shortDesc}
        </p>

        <div className="border-t border-gray-100 pt-4 mt-auto" data-id="element-166">
          <button onClick={e => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }} className="w-full flex items-center justify-between text-sm font-medium text-[#7a8a6b] hover:text-[#5d6e4f] transition-colors mb-4" data-id="element-167">
            <span data-id="element-168">Más información</span>
            <motion.div animate={{
            rotate: isOpen ? 180 : 0
          }} data-id="element-169">
              <ChevronDownIcon className="w-4 h-4" data-id="element-170" />
            </motion.div>
          </button>

          <AnimatePresence data-id="element-171">
            {isOpen && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="overflow-hidden mb-4" data-id="element-172">
                <p className="text-sm text-[#2d2d2d] leading-relaxed" data-id="element-173">
                  {bump.details}
                </p>
              </motion.div>}
          </AnimatePresence>

          <div className={`flex items-center justify-center gap-2 py-3 rounded-lg font-bold transition-colors ${selected ? 'bg-[#5d6e4f]/10 text-[#5d6e4f]' : 'bg-amber-50 text-amber-700'}`} data-id="element-174">
            <div className={`w-5 h-5 rounded border flex items-center justify-center ${selected ? 'bg-[#5d6e4f] border-[#5d6e4f]' : 'border-amber-400 bg-white'}`} data-id="element-175">
              {selected && <CheckIcon className="w-3 h-3 text-white" data-id="element-176" />}
            </div>
            <span data-id="element-177">
              {selected ? '✓ ¡Agregado a tu pedido!' : `Agrégalo por $${bump.price} USD`}
            </span>
          </div>
        </div>
      </div>
    </motion.div>;
}